import { useEffect, useRef, useState } from "react";
import { sendChat } from "@/lib/api";
import { Sparkles, Send, Loader2, MapPin, RotateCcw } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

const SESSION_KEY = "lyve.chat.session";
const STARTERS = [
    "Find me a kickboxing class opening soonest",
    "Cheap pilates in Brooklyn under $30",
    "Beginner-friendly prenatal yoga this week",
    "Anything open right now near Williamsburg?",
];

/**
 * Lyve AI — scoped chat that sits beside the directory.
 *
 * The model is restricted by the backend system prompt to NYC women's fitness
 * topics. Off-topic prompts get a one-line decline.
 *
 * Recommended classes returned by the model are rendered inline as compact
 * cards; clicking one opens the full ClassDetailModal via `onPickClass`.
 *
 * If the user has shared their location (userPos), we pass it on every turn
 * so "near me" queries work naturally.
 */
export function ChatPanel({ userPos, onPickClass, onLocateMe }) {
    const [sessionId, setSessionId] = useState(() => {
        try {
            return localStorage.getItem(SESSION_KEY) || "";
        } catch {
            return "";
        }
    });
    const [messages, setMessages] = useState([]); // {role, text, classes?, error?}
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const listRef = useRef(null);

    // Auto-scroll to the newest message.
    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages, sending]);

    const submit = async (raw) => {
        const text = (raw ?? input).trim();
        if (!text || sending) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text }]);
        setSending(true);
        try {
            const resp = await sendChat({
                message: text,
                sessionId: sessionId || undefined,
                userLat: userPos?.[0],
                userLng: userPos?.[1],
            });
            if (resp.session_id && resp.session_id !== sessionId) {
                setSessionId(resp.session_id);
                try {
                    localStorage.setItem(SESSION_KEY, resp.session_id);
                } catch {
                    // ignore
                }
            }
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    text: resp.reply,
                    classes: resp.recommended_classes || [],
                },
            ]);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Couldn't reach the assistant.";
            setMessages((m) => [
                ...m,
                { role: "assistant", text: msg, error: true },
            ]);
            toast.error("Chat error — try again in a moment.");
        } finally {
            setSending(false);
        }
    };

    const reset = () => {
        setMessages([]);
        setSessionId("");
        try {
            localStorage.removeItem(SESSION_KEY);
        } catch {
            // ignore
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
        }
    };

    return (
        <aside
            className="border border-foreground bg-background flex flex-col h-[680px] lg:h-[760px]"
            data-testid="chat-panel"
        >
            <header className="px-5 py-4 border-b border-foreground bg-foreground text-background flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="inline-flex h-7 w-7 items-center justify-center border border-background">
                        <Sparkles className="h-3.5 w-3.5" />
                    </span>
                    <div>
                        <div className="font-display text-lg leading-none">
                            Lyve <span className="font-editorial text-primary">AI</span>
                        </div>
                        <div className="font-mono-label text-[9px] uppercase tracking-widest opacity-70 mt-0.5">
                            NYC fitness only
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {!userPos && (
                        <button
                            onClick={onLocateMe}
                            className="px-2 py-1 font-mono-label text-[9px] uppercase tracking-widest border border-background text-background hover:bg-background hover:text-foreground flex items-center gap-1"
                            data-testid="chat-locate-btn"
                            title="Share your location for 'near me' answers"
                        >
                            <MapPin className="h-3 w-3" /> Locate
                        </button>
                    )}
                    {messages.length > 0 && (
                        <button
                            onClick={reset}
                            className="px-2 py-1 font-mono-label text-[9px] uppercase tracking-widest border border-background text-background hover:bg-background hover:text-foreground flex items-center gap-1"
                            data-testid="chat-reset"
                        >
                            <RotateCcw className="h-3 w-3" /> Reset
                        </button>
                    )}
                </div>
            </header>

            <div ref={listRef} className="flex-1 overflow-y-auto p-5 space-y-5" data-testid="chat-messages">
                {messages.length === 0 && (
                    <div className="space-y-5">
                        <div className="space-y-2">
                            <p className="font-editorial text-2xl leading-tight">
                                Ask me anything <span className="text-primary">about classes.</span>
                            </p>
                            <p className="text-sm text-muted-foreground">
                                I know every curated NYC studio — schedules, categories,
                                prices, and what's opening next. I'll politely pass on
                                anything outside fitness.
                            </p>
                        </div>
                        <div className="space-y-2" data-testid="chat-starters">
                            {STARTERS.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => submit(s)}
                                    className="block w-full text-left text-sm border border-border bg-secondary hover:border-foreground hover:bg-background px-3 py-2.5 transition-colors"
                                    data-testid={`chat-starter-${s.slice(0, 10).replace(/\s+/g, "-").toLowerCase()}`}
                                >
                                    <span className="font-mono-label text-[9px] uppercase tracking-widest text-primary mr-2">
                                        Try
                                    </span>
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((m, i) => (
                    <ChatBubble
                        key={i}
                        msg={m}
                        onPickClass={onPickClass}
                    />
                ))}

                {sending && (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm" data-testid="chat-typing">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Lyve is thinking…
                    </div>
                )}
            </div>

            <form
                onSubmit={(e) => { e.preventDefault(); submit(); }}
                className="border-t border-foreground p-3 flex items-center gap-2 bg-background"
            >
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Ask about a class, schedule, or category…"
                    className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground py-2 px-2"
                    data-testid="chat-input"
                    disabled={sending}
                    maxLength={2000}
                />
                <button
                    type="submit"
                    disabled={sending || !input.trim()}
                    className="px-4 py-2 bg-foreground text-background font-mono-label text-[10px] uppercase tracking-widest border border-foreground hover-invert disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="chat-send"
                >
                    {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    Send
                </button>
            </form>
        </aside>
    );
}

function ChatBubble({ msg, onPickClass }) {
    const isUser = msg.role === "user";
    return (
        <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
            <div
                className={`max-w-[88%] ${
                    isUser
                        ? "bg-foreground text-background px-4 py-3"
                        : "bg-background text-foreground px-0 py-0"
                }`}
                data-testid={`chat-${msg.role}-bubble`}
            >
                <p className={`whitespace-pre-wrap text-sm leading-relaxed ${msg.error ? "text-primary" : ""}`}>
                    {msg.text}
                </p>
                {msg.classes && msg.classes.length > 0 && (
                    <div className="mt-3 space-y-2 border-l-2 border-primary pl-3">
                        <p className="font-mono-label text-[9px] uppercase tracking-widest text-muted-foreground">
                            Recommended · {msg.classes.length}
                        </p>
                        {msg.classes.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => onPickClass?.(c)}
                                className="block w-full text-left border border-border hover:border-foreground bg-secondary hover:bg-background transition-colors px-3 py-2.5"
                                data-testid={`chat-rec-${c.id}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="font-display text-sm leading-tight truncate">
                                            {c.class_name}
                                        </p>
                                        <p className="font-editorial text-xs text-muted-foreground truncate">
                                            at {c.studio_name} · {c.neighborhood || c.borough}
                                        </p>
                                    </div>
                                    {c.class_status && (
                                        <StatusBadge status={c.class_status} size="compact" />
                                    )}
                                </div>
                                <div className="mt-1.5 flex items-center gap-3 text-[10px] font-mono-label text-muted-foreground">
                                    <span>{c.category}</span>
                                    {c.price_per_class && <span>{c.price_per_class}</span>}
                                    {c.duration_minutes && <span>{c.duration_minutes} min</span>}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
